import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomLayout from "../../Layout/CustomLayout";
import {
  Typography,
  Card,
  Row,
  Col,
  DatePicker,
  Button,
  Image,
  Table,
  Input,
  Space,
  Select,
  message,
} from "antd";
import { SearchOutlined, CloudDownloadOutlined } from "@ant-design/icons";
import typoStyles from "../../../styles/Custom/Typography.module.css";
import NoDataPage from "../../NoDataPages/NoDataPage";
import investigationImage from "../../../assets/images/nurseIcons/investigation.svg";
import inputStyles from "../../../styles/Custom/Input.module.css";
import calendarImg from "../../../assets/images/nurseIcons/calender.svg";
import { getOperationsReportAPI } from "../../../services/orgDashboard.services";
import { getFacilityListApi } from "../../../services/facilities.services";
import { getCookie } from "../../../helpers/localStorage";
import { FacilityID, Role } from "../../../constants/defaultKeys";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import axiosInstance from "../../../config/axios.config";
import SideMenu from "../../Layout/CustomLayout/SideMenu";
import CollectionsTable from "./CollectionsTable";
import useQuery from "../../../hooks/useQuery";
import {
  parsePageParam,
  parseSearchParam,
  updateUrlSearchParams,
} from "../../../helpers/urlFilters";
import { formatPaymentModeLabel } from "../../../helpers/utility";

const { RangePicker } = DatePicker;
const dateFormat = "YYYY-MM-DD";

const disabledDate = (current) => {
  return current && current > dayjs().endOf("day");
};

// The report columns come straight from the API payload, whose keys are
// Title_Case. Match them loosely so a key spelled "Payment_Mode", "mode" or
// "Payment Mode" is all recognised as the payment mode column.
const normalizeReportKey = (key) =>
  String(key ?? "").toLowerCase().replace(/[^a-z]/g, "");

const PAYMENT_MODE_KEYS = ["paymentmode", "mode"];
const OTHER_MODE_KEYS = [
  "othermode",
  "otherpaymentmode",
  "paymentothermode",
  "othermodename",
];

const isPaymentModeColumn = (key) =>
  PAYMENT_MODE_KEYS.includes(normalizeReportKey(key));

const isOtherModeColumn = (key) =>
  OTHER_MODE_KEYS.includes(normalizeReportKey(key));

const getOtherModeValue = (record) => {
  const match = Object.keys(record || {}).find((key) =>
    isOtherModeColumn(key)
  );
  return match ? record[match] : "";
};

dayjs.extend(isoWeek);

const Operations = () => {
  const navigate = useNavigate();
  const params = useQuery();
  const [singleFacilityName, setSingleFacilityName] = useState(null);
  const facilityAdmin =
    getCookie(Role) === "Facility_Admin" ? getCookie(FacilityID) : null;
  const endOfMonth = dayjs().endOf("month");

  const reportType = params?.report_type || "ip_payments";
  const searchText = parseSearchParam(params) || "";
  const page = parsePageParam(params);
  const currentFacility = facilityAdmin
    ? Number(facilityAdmin)
    : params?.facility
      ? Number(params.facility)
      : null;
  const date = {
    start_date: params?.start_date || "",
    end_date: params?.end_date || "",
  };

  const [searchInput, setSearchInput] = useState(searchText);
  const [facilityData, setFacilityData] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: page,
    pageSize: 100,
    total: 0,
  });
  const debounceRef = useRef(null);

  // Report type options
  const reportTypeOptions = [
    { label: "IP Payments", value: "ip_payments" },
    { label: "IP Other Services", value: "ip_other_services" },
    {
      label: "IP Other Services Active Patients",
      value: "ip_other_services_active_patients",
    },
    { label: "OP Payments", value: "op_payments" },
    { label: "OP Bills", value: "op_bills" },
    { label: "OP Packages", value: "op_packages" },
    { label: "Collections", value: "collections" },
  ];

  useEffect(() => {
    setSearchInput(searchText);
  }, [searchText]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, current: page }));
  }, [page]);

  useEffect(() => {
    getFacilityListApi()
      .then((res) => {
        const data = res?.data || [];

        const facilityOptions = data.map((option) => ({
          label: option.name,
          value: option.id,
        }));

        if (facilityOptions.length === 1) {
          setSingleFacilityName(facilityOptions[0].label);
          setFacilityData(facilityOptions);
        } else {
          setFacilityData([{ label: "All", value: null }, ...facilityOptions]);
        }
      })
      .catch((error) => console.log(`Error:${error}`));
  }, []);

  const onDateChange = (range) => {
    if (!range?.[0] || !range?.[1]) return;
    updateUrlSearchParams(navigate, {
      start_date: dayjs(range[0]).format(dateFormat),
      end_date: dayjs(range[1]).format(dateFormat),
      page: 1,
    });
  };

  const fetchOperationsReport = async (pageNum = page, pageSize = 100) => {
    setLoading(true);
    getOperationsReportAPI(
      reportType,
      currentFacility,
      date,
      pageNum,
      pageSize,
      searchText
    )
      .then((res) => {
        setData(res?.data?.data || []);
        setPagination({
          current: res?.data?.current_page || pageNum,
          pageSize: pageSize,
          total: res?.data?.total_count || 0,
        });
      })
      .catch((error) => console.log(`Error:${error}`))
      .finally(() => setLoading(false));
  };

  const handleTableChange = (nextPagination) => {
    updateUrlSearchParams(navigate, { page: nextPagination.current });
    fetchOperationsReport(nextPagination.current, nextPagination.pageSize);
  };

  const handleSearchChange = (value) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      updateUrlSearchParams(navigate, { search: value || null, page: 1 });
    }, 1000);
  };

  useEffect(() => {
    if (date?.start_date && date?.end_date && reportType) {
      fetchOperationsReport(page, pagination.pageSize);
    }
    // eslint-disable-next-line
  }, [currentFacility, date.start_date, date.end_date, reportType, page, searchText]);

  // Generate dynamic columns based on data structure
  const generateColumns = () => {
    if (!data || data.length === 0) return [];

    const sampleRecord = data[0];
    const allKeys = Object.keys(sampleRecord);
    const hasPaymentModeColumn = allKeys.some(isPaymentModeColumn);

    return allKeys
      // The "others" name is folded into the payment mode column below, so
      // drop its own column rather than showing the same value twice.
      .filter((key) => !(hasPaymentModeColumn && isOtherModeColumn(key)))
      .map((key, index) => ({
        title: key.replace(/_/g, " "),
        dataIndex: key,
        key: `${key}_${index}`,
        width: 150,
        fixed: key === "Admission_No" ? "left" : undefined,
        render: (text, record) => {
          if (isPaymentModeColumn(key)) {
            return formatPaymentModeLabel(text, getOtherModeValue(record));
          }
          if (typeof text === "number" && key.toLowerCase().includes("amount")) {
            return `₹${text.toLocaleString()}`;
          }
          return text;
        },
      }));
  };

  const columns = generateColumns();

  const handleExport = async (type) => {
    if (!date?.start_date) return message.error("Please select a date range");
    try {
      const response = await axiosInstance.get(
        `/billing/dashboard/${reportType}/`,
        {
          params: {
            facility: currentFacility,
            start_date: date?.start_date,
            end_date: date?.end_date,
            response_type: type,
          },
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data], {
        type:
          type === "xlsx"
            ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            : "text/csv",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `operations_${reportType}_${currentFacility}_${dayjs().format(
        "YYYY-MM-DD"
      )}.${type}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed:", error);

      message.error(!date?.start_date ? "Please select a date range" : "");
    }
  };

  const handleChange = (e) => {
    handleSearchChange(e.target.value);
  };

  return (
    <CustomLayout
      sideContent={<SideMenu />}
      header={
        <Typography.Title
          level={3}
          className={typoStyles.typoHeadingLarge}
          style={{ margin: 0 }}
        >
          Operations
        </Typography.Title>
      }
    >
      <Card
        style={{ borderRadius: 16, border: "1px solid #e6e6e6", margin: 16 }}
        // title={
        //   <Space>
        //     <Typography.Text strong>Operations Report</Typography.Text>
        //     {date?.start_date && date?.end_date && (
        //       <Tag color="blue">
        //         {dayjs(date.start_date).format("MMM DD, YYYY")} -{" "}
        //         {dayjs(date.end_date).format("MMM DD, YYYY")}
        //       </Tag>
        //     )}
        //   </Space>
        // }
      >
        <Row gutter={8} align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Report Type</div>
            <Select
              value={reportType}
              options={reportTypeOptions}
              onChange={(val) =>
                updateUrlSearchParams(navigate, {
                  report_type: val,
                  page: 1,
                  search: null,
                })
              }
              style={{ width: 250, marginRight: 8 }}
              size="large"
            />
          </Col>
          {!facilityAdmin && (
            <Col>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>Facility</div>

              {singleFacilityName ? (
                <Typography.Text
                  style={{
                    display: "inline-block",
                    padding: "6px 12px",
                    borderRadius: 6,
                    background: "#f5f5f5",
                    fontWeight: 500,
                  }}
                >
                  {singleFacilityName}
                </Typography.Text>
              ) : (
                <Select
                  value={currentFacility}
                  options={facilityData}
                  onChange={(val) =>
                    updateUrlSearchParams(navigate, { facility: val, page: 1 })
                  }
                  style={{ width: 150 }}
                  size="large"
                  loading={loading}
                />
              )}
            </Col>
          )}
          <Col xs={24} sm={12} md={8} lg={6}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>
              Start Date & End Date
            </div>
            <RangePicker
              style={{
                color: "black",
                border:
                  "1px solid color-mix(in srgb, var(--ant-primary-color) 24%, white)",
                height: "38px",
              }}
              disabledDate={(current) =>
                current && current.isAfter(endOfMonth, "day")
              }
              className={inputStyles.mdDateInput}
              onChange={onDateChange}
              allowClear={false}
              value={[
                date?.start_date ? dayjs(date.start_date) : null,
                date?.end_date ? dayjs(date.end_date) : null,
              ]}
              suffixIcon={
                <Image width={20} src={calendarImg} preview={false} />
              }
            />
          </Col>
          <Col flex="auto" />
          <Col>
            {reportType !== "collections" && (
              <Input
                placeholder="Search Records"
                prefix={<SearchOutlined />}
                style={{
                  width: 220,
                  marginTop: 24,
                  borderRadius: 8,
                }}
                value={searchInput}
                onChange={handleChange}
                allowClear
              />
            )}
          </Col>
          <Col>
            <Space style={{ marginTop: 24 }}>
              <Button
                icon={<CloudDownloadOutlined />}
                style={{
                  background:
                    "color-mix(in srgb, var(--ant-primary-color) 10%, white)",
                  color: "var(--ant-primary-color)",
                  border: 0,
                  borderRadius: 8,
                  fontWeight: 500,
                }}
                onClick={() => handleExport("xlsx")}
              >
                Excel
              </Button>
              <Button
                icon={<CloudDownloadOutlined />}
                type="primary"
                onClick={() => handleExport("csv")}
              >
                CSV
              </Button>
            </Space>
          </Col>
        </Row>
        {/* {data.length === 0 ? (
          <Row
            justify="center"
            align="middle"
            style={{ minHeight: "60vh", width: "100%" }}
          >
            <Col>
              <NoDataPage
                title="No Data found"
                subtitle="Select a report type and date range to view data"
                image={investigationImage}
              />
            </Col>
          </Row>
        ) : ( */}
        {reportType === "collections" ? (
          <CollectionsTable
            data={data}
            pagination={pagination}
            handleTableChange={handleTableChange}
          />
        ) : (
          <Table
            columns={columns}
            dataSource={data}
            rowKey={(record, index) =>
              `${record.Admission_No || record.Patient_Id || "row"}_${index}`
            }
            loading={loading}
            scroll={{ x: "max-content" }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: false,
              position: ["bottomCenter"],
            }}
            onChange={handleTableChange}
            locale={{
              emptyText: (
                <NoDataPage
                  title="No Data found"
                  subtitle="Select a report type and date range to view data"
                  image={investigationImage}
                />
              ),
            }}
          />
        )}

        {/* )} */}
      </Card>
    </CustomLayout>
  );
};

export default Operations;
